import { useEffect, useState } from 'react';
import { apiFetch } from '../config/api';
import { StatSection } from './StatTile';

/**
 * Who has updated, and who has not.
 *
 * Counts come from the version each app reports when it checks for updates, so
 * "not reported" is a real bucket rather than a rounding error — someone who
 * has not opened the app since tracking shipped has never told us anything.
 * It is shown as its own number instead of being folded into the current
 * version, because overstating adoption defeats the point of the screen.
 */

function VersionBar({ row, max }) {
  const width = max > 0 ? Math.max((row.people / max) * 100, 2) : 0;
  const tone = row.is_latest ? '#16a34a' : (row.is_outdated ? '#f59e0b' : '#9ca3af');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
      <div style={{ width: 78, fontSize: 13, fontWeight: 600, color: '#111827' }}>
        {row.version}
        {row.is_latest ? (
          <span style={{ marginLeft: 5, fontSize: 10, color: '#16a34a', fontWeight: 700 }}>NEW</span>
        ) : null}
      </div>

      <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 4, height: 18, position: 'relative' }}>
        <div style={{ width: `${width}%`, background: tone, height: '100%', borderRadius: 4 }} />
      </div>

      <div style={{ width: 54, textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#111827' }}>
        {row.people.toLocaleString()}
      </div>
      <div style={{ width: 96, textAlign: 'right', fontSize: 11, color: '#6b7280' }}>
        {row.ios ? `${row.ios} iOS` : ''}
        {row.ios && row.android ? ' · ' : ''}
        {row.android ? `${row.android} Android` : ''}
      </div>
    </div>
  );
}

function AppBreakdown({ title, population, data }) {
  if (!data) return null;

  const max = data.versions.reduce((highest, row) => Math.max(highest, row.people), 0);
  const hasReadings = data.versions.length > 0;

  return (
    <div className="sa-card" style={{ padding: '14px 16px', flex: '1 1 380px', minWidth: 320 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{title}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          {data.latest_version ? `Latest ${data.latest_version}` : 'No release configured'}
        </div>
      </div>

      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
        {data.on_latest_percent === null
          ? 'No app has reported a version yet.'
          : `${data.on_latest_percent}% of those reporting are on the latest build`}
        {data.outdated > 0 ? ` · ${data.outdated.toLocaleString()} behind` : ''}
      </div>

      {hasReadings ? (
        <div>
          {data.versions.map((row) => (
            <VersionBar key={row.version} row={row} max={max} />
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: '#6b7280', padding: '8px 0' }}>
          Nothing reported in the last {data.stale_after_days} days.
        </div>
      )}

      {/* Spell out the denominator: it is not the "Total Parents"/"Total Staff"
          tile above. The parent app is also used by guardians and drivers, and
          staff are counted as people rather than per-school rows, so an
          unqualified "total" here reads as a contradiction. */}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6', fontSize: 12, color: '#6b7280' }}>
        {data.reported_people.toLocaleString()} reporting
        {' · '}
        {data.unknown_people.toLocaleString()} not reported
        {' · '}
        {data.total_people.toLocaleString()} {population}
      </div>
    </div>
  );
}

export default function AppVersionStats({ token }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiFetch('/platform-stats/app-versions', {}, token)
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch((err) => {
        // A school-level admin (403) simply does not see the section, matching
        // how the platform overview above behaves.
        if (!cancelled && err.status !== 403) setError(err.message);
      });
    return () => { cancelled = true; };
  }, [token]);

  if (error) return <div className="sa-error">{error}</div>;
  if (!stats) return null;

  return (
    <StatSection title="App versions">
      <AppBreakdown
        title="Parent app"
        population="parents, guardians & drivers"
        data={stats.parent}
      />
      <AppBreakdown
        title="Staff app"
        population="active staff"
        data={stats.staff}
      />
    </StatSection>
  );
}
